import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../ui/card";
import {
  CheckCircle,
  XCircle,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Mail,
  AlertTriangle,
  Check,
  X,
  Car,
  UserPlus,
  Users,
} from "lucide-react";
import useUser from "../../api/user";
import useParking from "../../api/parking";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const RegistrationRequestsManagement = ({ onRequestProcessed }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredRequests, setFilteredRequests] = useState([]);
  const userApi = useUser();
  const parkingApi = useParking();
  const [requests, setRequests] = useState([]);
  const [requestsWithParking, setRequestsWithParking] = useState([]);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showFilters, setShowFilters] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    action: null,
    requestId: null,
    actionType: "", // "approve" or "reject"
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage, setRequestsPerPage] = useState(5);

  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      // Set fewer items per page on mobile
      setRequestsPerPage(mobile ? 3 : 5);
    };

    handleResize(); // Set initial values
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchRegistrationRequests();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    applyAllFilters();
  }, [searchQuery, requests]);

  const fetchRegistrationRequests = async () => {
    try {
      const users = await userApi.getAllUsers();
      const parkingSpots = await parkingApi.getAllParkingSpots();

      // Filter only registration requests (status = 0 && accepted = 0)
      const pendingRequests = users.filter(
        (user) => user.status == "0" && user.accepted == "0"
      );

      // Combine users with their parking spots
      const requestsWithParkingData = pendingRequests.map((user) => {
        const userParkingSpot = parkingSpots.spots.find(
          (spot) => spot.owner_id === user.id
        );
        return {
          ...user,
          parkingSpot: userParkingSpot || null,
        };
      });

      console.log(requestsWithParkingData);

      setRequests(pendingRequests);
      setRequestsWithParking(requestsWithParkingData);
      setFilteredRequests(requestsWithParkingData);
    } catch (error) {
      console.error(t("management.registrations.errors.fetchRequests"), error);
    }
  };

  const applyAllFilters = () => {
    let filtered = requestsWithParking.filter((request) => {
      // Apply search filter if query exists
      return (
        !searchQuery ||
        request.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.parkingSpot?.spot_number
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    });

    setFilteredRequests(filtered);
  };

  // Handle approve/reject request
  const handleRequestAction = async (requestId, action) => {
    try {
      if (action === "approve") {
        await userApi.approveRegistration(requestId);
      } else if (action === "reject") {
        await userApi.rejectRegistration(requestId);
      }

      // Remove from the current list
      const updatedRequests = requests.filter((req) => req.id !== requestId);
      const updatedRequestsWithParking = requestsWithParking.filter(
        (req) => req.id !== requestId
      );
      setRequests(updatedRequests);
      setRequestsWithParking(updatedRequestsWithParking);

      // Notify parent component about the processed request
      if (onRequestProcessed) {
        onRequestProcessed();
      }

      setConfirmDialog({ ...confirmDialog, isOpen: false });
    } catch (error) {
      console.error(
        t(
          `management.registrations.errors.${
            action === "approve" ? "approve" : "reject"
          }`
        ),
        error
      );
    }
  };

  // Open confirmation dialog
  const openConfirmDialog = (request, actionType) => {
    const isApprove = actionType === "approve";
    setConfirmDialog({
      isOpen: true,
      title: isApprove
        ? t("management.registrations.dialog.approveTitle")
        : t("management.registrations.dialog.rejectTitle"),
      description: isApprove
        ? t("management.registrations.dialog.approveDescription", {
            username: request.username,
          })
        : t("management.registrations.dialog.rejectDescription", {
            username: request.username,
          }),
      action: () => handleRequestAction(request.id, actionType),
      requestId: request.id,
      actionType,
    });
  };

  // Get current requests for pagination
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstRequest,
    indexOfLastRequest
  );

  // Calculate total pages
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  // Change page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Go to specific page
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers - limit visible pages on mobile
  const getPageNumbers = () => {
    if (isMobileView) {
      // On mobile, show fewer page numbers
      if (totalPages <= 3) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      } else {
        // Show current page and one on each side when possible
        if (currentPage === 1) {
          return [1, 2, 3];
        } else if (currentPage === totalPages) {
          return [totalPages - 2, totalPages - 1, totalPages];
        } else {
          return [currentPage - 1, currentPage, currentPage + 1];
        }
      }
    } else {
      // On desktop, show all pages (or up to 5)
      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      } else {
        // More than 5 pages, show current page and 2 on each side when possible
        const startPage = Math.max(1, currentPage - 2);
        const endPage = Math.min(totalPages, startPage + 4);
        return Array.from(
          { length: endPage - startPage + 1 },
          (_, i) => startPage + i
        );
      }
    }
  };

  const pageNumbers = getPageNumbers();

  // Mobile card view component
  const RequestCard = ({ request }) => (
    <Card className="mb-3 overflow-hidden">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="font-semibold">{request.username}</h3>
            <div className="flex items-center gap-1 mt-1">
              <Mail className="w-3 h-3 text-neutral-500" />
              <span className="text-xs text-neutral-600 dark:text-neutral-400 truncate max-w-[180px]">
                {request.email}
              </span>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger className="p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800">
              <MoreHorizontal className="h-5 w-5 text-neutral-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => openConfirmDialog(request, "approve")}
                className="text-green-600"
              >
                <Check className="w-4 h-4 mr-2" />{" "}
                {t("management.registrations.actions.approve")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => openConfirmDialog(request, "reject")}
                className="text-red-600"
              >
                <X className="w-4 h-4 mr-2" />{" "}
                {t("management.registrations.actions.reject")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              <AlertTriangle className="w-3 h-3" />{" "}
              {t("management.registrations.status.pending")}
            </span>
            <Badge className="bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800">
              {request.first_name} {request.last_name}
            </Badge>
            {request.parkingSpot && (
              <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30">
                <Car className="w-3 h-3 mr-1" />
                {request.parkingSpot.spot_number}
              </Badge>
            )}
          </div>
        </div>

        <div className="mt-3 text-sm">
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <span className="text-neutral-500 dark:text-neutral-400">
                {t("management.registrations.requestedAt")}
              </span>
              <span className="font-medium">
                {new Date(request.created_at).toLocaleDateString()}
              </span>
            </div>
            {request.parkingSpot && (
              <div className="flex justify-between">
                <span className="text-neutral-500 dark:text-neutral-400">
                  Parking
                </span>
                <span className="font-medium">
                  Floor {request.parkingSpot.floor} -{" "}
                  {request.parkingSpot.spot_number}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button
            onClick={() => openConfirmDialog(request, "approve")}
            className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-1"
            size="sm"
          >
            <Check className="w-4 h-4" />{" "}
            {t("management.registrations.actions.approve")}
          </Button>
          <Button
            onClick={() => openConfirmDialog(request, "reject")}
            className="bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-1"
            size="sm"
          >
            <X className="w-4 h-4" />{" "}
            {t("management.registrations.actions.reject")}
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-4 md:space-y-6 p-2 md:p-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">
            {t("management.registrations.title")}
          </h2>
          <p className="text-sm text-muted-foreground hidden md:block">
            {t("management.registrations.description")}
          </p>
        </div>
        <Badge className="bg-yellow-500 text-white text-sm">
          {filteredRequests.length}{" "}
          {t("management.registrations.status.pending")}
        </Badge>
      </div>

      {/* Search and Filter Toggle for Mobile */}
      <div className="flex gap-2 md:hidden">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
          <input
            type="text"
            placeholder={t("management.registrations.searchPlaceholder")}
            className="pl-10 pr-4 py-2 w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`p-2 rounded-lg ${
            showFilters
              ? "bg-primary text-white"
              : "bg-neutral-100 dark:bg-neutral-800"
          }`}
        >
          <Filter className="w-5 h-5" />
        </button>
      </div>

      {/* Expandable Filters on Mobile */}
      {(showFilters || !isMobileView) && (
        <Card
          className={`p-4 bg-white dark:bg-neutral-900 ${
            isMobileView ? "animate-in slide-in-from-top duration-300" : ""
          }`}
        >
          <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-4">
            {/* Search - Only visible on desktop */}
            <div className="relative flex-1 hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
              <input
                type="text"
                placeholder={t(
                  "management.registrations.searchPlaceholderDetailed"
                )}
                className="pl-10 pr-4 py-2 w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Mobile Card View */}
      {isMobileView ? (
        <div className="space-y-1">
          {currentRequests.length > 0 ? (
            currentRequests.map((request) => (
              <RequestCard key={`mobile-${request.id}`} request={request} />
            ))
          ) : (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                {t("management.registrations.noRequests")}
              </p>
            </Card>
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {t("management.registrations.table.username")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {t("management.registrations.table.fullName")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {t("management.registrations.table.email")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Parking Spot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {t("management.registrations.table.requestedDate")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {t("management.registrations.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {t("management.registrations.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {currentRequests.length > 0 ? (
                  currentRequests.map((request) => (
                    <tr
                      key={request.id}
                      className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        {request.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.first_name} {request.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.parkingSpot ? (
                          <div className="flex items-center gap-2">
                            <Car className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">
                              {request.parkingSpot.spot_number}
                            </span>
                            <span className="text-sm text-neutral-500">
                              (Floor {request.parkingSpot.floor})
                            </span>
                          </div>
                        ) : (
                          <span className="text-neutral-400 italic">
                            No parking assigned
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                          <AlertTriangle className="w-3 h-3" />{" "}
                          {t("management.registrations.status.pending")}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            className="flex items-center gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                            onClick={() =>
                              openConfirmDialog(request, "approve")
                            }
                          >
                            <Check className="w-3 h-3" />
                            {t("management.registrations.actions.approve")}
                          </button>
                          <button
                            className="flex items-center gap-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-1 rounded hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                            onClick={() => openConfirmDialog(request, "reject")}
                          >
                            <X className="w-3 h-3" />
                            {t("management.registrations.actions.reject")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-12 text-center text-muted-foreground"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <UserPlus className="h-12 w-12 text-gray-300 mb-4" />
                        <p className="text-lg font-medium text-gray-500">
                          {t("management.registrations.noRequestsHeader")}
                        </p>
                        <p className="text-sm text-gray-400">
                          {t("management.registrations.noRequestsSubheader")}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination Controls - Responsive */}
      {filteredRequests.length > 0 && (
        <Card className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 hidden sm:block">
              {t("management.registrations.pagination.showing", {
                from: indexOfFirstRequest + 1,
                to: Math.min(indexOfLastRequest, filteredRequests.length),
                total: filteredRequests.length,
              })}
            </div>

            <div className="flex items-center space-x-1 md:space-x-2">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`p-1 rounded-md ${
                  currentPage === 1
                    ? "text-neutral-300 dark:text-neutral-600 cursor-not-allowed"
                    : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                }`}
                aria-label={t("management.registrations.pagination.previous")}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              {pageNumbers.map((number) => (
                <button
                  key={number}
                  onClick={() => goToPage(number)}
                  className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                    currentPage === number
                      ? "bg-primary text-white"
                      : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  }`}
                  aria-label={t(
                    "management.registrations.pagination.pageNumber",
                    { number }
                  )}
                >
                  {number}
                </button>
              ))}

              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`p-1 rounded-md ${
                  currentPage === totalPages
                    ? "text-neutral-300 dark:text-neutral-600 cursor-not-allowed"
                    : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                }`}
                aria-label={t("management.registrations.pagination.next")}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, isOpen: open })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogDescription>{confirmDialog.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({ ...confirmDialog, isOpen: false })
              }
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={confirmDialog.action}
              className={
                confirmDialog.actionType === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {confirmDialog.actionType === "approve"
                ? t("management.registrations.actions.approve")
                : t("management.registrations.actions.reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationRequestsManagement;
